import React from "react";
import { useNavigate } from "react-router-dom";

import {
  CopyToClipboardButton,
  Link,
  ShimmeredDetailsListProps,
  Stack,
  Text,
  TextBadge,
  TextBadgeColor,
  TextBadgeSize
} from "@bps/fluent-ui";
import { DataTable, DataTableColumn } from "@components/tables/DataTable";
import {
  FieldDevice,
  TenantDeploymentRingSummary
} from "@libs/api/gateways/field/field-ops-gateway.dtos";

interface DeviceTenantDeploymentRingTableProps
  extends ShimmeredDetailsListProps {
  device: FieldDevice;
  items: TenantDeploymentRingSummary[];
}

export const DeviceTenantDeploymentRingTable: React.FC<DeviceTenantDeploymentRingTableProps> = ({
  device,
  items,
  ...props
}) => {
  const navigate = useNavigate();

  const columns: DataTableColumn<TenantDeploymentRingSummary>[] = [
    {
      key: "sortOrder",
      name: "Order",
      minWidth: 100,
      maxWidth: 100,
      isSorted: true,
      isSortedDescending: true,
      onRender: (
        fieldDeviceDeploymentRingDetail: TenantDeploymentRingSummary
      ) => <Text>{fieldDeviceDeploymentRingDetail.sortOrder}</Text>
    },
    {
      key: "name",
      name: "Deployment Ring Name",
      minWidth: 160,
      maxWidth: 160,
      onRender: (
        fieldDeviceDeploymentRingDetail: TenantDeploymentRingSummary
      ) => (
        <Link
          onClick={() =>
            navigate(
              `/system/plt/deployment-rings/${fieldDeviceDeploymentRingDetail.deploymentRingId}`
            )
          }
        >
          {fieldDeviceDeploymentRingDetail.deploymentRingDisplayName}
        </Link>
      )
    },
    {
      key: "description",
      name: "Deployment Ring Description",
      minWidth: 400,

      onRender: (
        fieldDeviceDeploymentRingDetail: TenantDeploymentRingSummary
      ) => (
        <Text>{fieldDeviceDeploymentRingDetail.deploymentRingDescription}</Text>
      )
    },
    {
      key: "deploymentRingId",
      name: "Deployment Ring Id",
      minWidth: 300,
      maxWidth: 300,
      onRender: (
        fieldDeviceDeploymentRingDetail: TenantDeploymentRingSummary
      ) => (
        <Stack
          horizontal
          horizontalAlign="space-between"
          verticalAlign="center"
        >
          <CopyToClipboardButton
            clipboardContent={fieldDeviceDeploymentRingDetail.deploymentRingId}
          />
          <Text>{fieldDeviceDeploymentRingDetail.deploymentRingId}</Text>
        </Stack>
      )
    },
    {
      key: "code",
      name: "Code",
      minWidth: 100,
      maxWidth: 200,
      onRender: (
        fieldDeviceDeploymentRingDetail: TenantDeploymentRingSummary
      ) => <Text>{fieldDeviceDeploymentRingDetail.deploymentRingCode}</Text>
    },
    {
      key: "Type",
      name: "Type",
      minWidth: 100,
      maxWidth: 200,
      onRender: (
        fieldDeviceDeploymentRingDetail: TenantDeploymentRingSummary
      ) => (
        <>
          {fieldDeviceDeploymentRingDetail.fieldDeviceRoleTypeSid ===
          "CLIENT" ? (
            <TextBadge
              badgeColor={TextBadgeColor.green}
              badgeSize={TextBadgeSize.small}
            >
              {fieldDeviceDeploymentRingDetail.fieldDeviceRoleTypeSid}
            </TextBadge>
          ) : (
            <TextBadge
              badgeColor={TextBadgeColor.yellow}
              badgeSize={TextBadgeSize.small}
            >
              {fieldDeviceDeploymentRingDetail.fieldDeviceRoleTypeSid}
            </TextBadge>
          )}
        </>
      )
    },
    {
      key: "TenantId",
      name: "TenantId",
      minWidth: 300,
      maxWidth: 300,
      onRender: (
        fieldDeviceDeploymentRingDetail: TenantDeploymentRingSummary
      ) => (
        <Stack
          horizontal
          horizontalAlign="space-between"
          verticalAlign="center"
        >
          <CopyToClipboardButton
            clipboardContent={fieldDeviceDeploymentRingDetail.tenantId}
          />

          <Link
            onClick={() =>
              navigate(
                `/tenants/${fieldDeviceDeploymentRingDetail.tenantId}/tenant-deployment-rings`
              )
            }
          >
            {fieldDeviceDeploymentRingDetail.tenantId}
          </Link>
        </Stack>
      )
    }
  ];

  return (
    <DataTable
      items={items}
      stickyHeader
      shimmerLines={5}
      columns={columns}
      onShouldVirtualize={() => true}
      {...props}
    />
  );
};
